import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft,
  Loader2,
  Package,
  Send,
  DollarSign,
  FileText,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { trpc } from '@/lib/trpc';
import { formatCurrency, formatDate } from '@/lib/utils';
import { SendConsignmentModal } from '@/components/trading/consignment/send-modal';
import { RecordSaleModal } from '@/components/trading/consignment/record-sale-modal';

const statusColors: Record<string, string> = {
  DRAFT: 'bg-gray-100 text-gray-700',
  SENT: 'bg-blue-100 text-blue-700',
  PARTIALLY_SOLD: 'bg-yellow-100 text-yellow-700',
  FULLY_SOLD: 'bg-green-100 text-green-700',
  SETTLED: 'bg-emerald-100 text-emerald-700',
  RETURNED: 'bg-orange-100 text-orange-700',
  CANCELLED: 'bg-red-100 text-red-700',
};

export function ConsignmentDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation('trading');
  const { t: tCommon } = useTranslation('common');

  const [sendModalOpen, setSendModalOpen] = useState(false);
  const [saleModalOpen, setSaleModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'items' | 'sales' | 'settlements'>('items');

  const { data: consignment, isLoading, refetch } = trpc.tradingConsignment.byId.useQuery(
    id!,
    { enabled: !!id }
  );

  const getStatusLabel = (status: string) => {
    const key = status.toLowerCase().replace(/_/g, '');
    const statusMap: Record<string, string> = {
      draft: 'status.draft',
      sent: 'status.sent',
      partiallysold: 'status.partiallySold',
      fullysold: 'status.fullySold',
      settled: 'status.settled',
      returned: 'status.returned',
      cancelled: 'status.cancelled',
    };
    return t(`consignment.${statusMap[key] || 'status.draft'}`);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!consignment) {
    return (
      <div className="text-center py-12">
        <p>{tCommon('notFound')}</p>
      </div>
    );
  }

  // Item values are commercial reference prices in each item's OWN currency —
  // items can legitimately mix currencies, so totals are kept per currency
  // (a single cross-currency sum labeled with items[0].currency would be
  // meaningless).
  const sumByCurrency = (
    qtyOf: (item: NonNullable<typeof consignment>['items'][number]) => number
  ) => {
    const totals = new Map<string, number>();
    for (const item of consignment.items) {
      const ccy = item.currency || 'EUR';
      totals.set(ccy, (totals.get(ccy) ?? 0) + qtyOf(item) * Number(item.unitCost));
    }
    return totals;
  };
  const formatTotals = (totals: Map<string, number>) =>
    totals.size === 0
      ? '-'
      : [...totals.entries()].map(([ccy, value]) => formatCurrency(value, ccy)).join(' + ');
  const totalValue = sumByCurrency((i) => Number(i.quantitySent));
  const soldValue = sumByCurrency((i) => Number(i.quantitySold));
  const remainingValue = new Map(
    [...totalValue.entries()].map(([ccy, value]) => [ccy, value - (soldValue.get(ccy) ?? 0)])
  );

  const canSend = consignment.status === 'DRAFT';
  const canRecordSale = ['SENT', 'PARTIALLY_SOLD'].includes(consignment.status);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/trading/consignment')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold">{consignment.consignmentNumber}</h1>
              <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium ${statusColors[consignment.status]}`}>
                {getStatusLabel(consignment.status)}
              </span>
            </div>
            <p className="text-muted-foreground">
              {consignment.vendor.companyName} - {consignment.warehouse.name}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          {canSend && (
            <Button onClick={() => setSendModalOpen(true)}>
              <Send className="mr-2 h-4 w-4" />
              {t('consignment.detail.send')}
            </Button>
          )}
          {canRecordSale && (
            <Button onClick={() => setSaleModalOpen(true)}>
              <DollarSign className="mr-2 h-4 w-4" />
              {t('consignment.detail.recordSale')}
            </Button>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <div className="border border-border bg-card p-4">
          <div className="pb-2">
            <span className="text-sm font-medium">{t('consignment.detail.totalValue')}</span>
          </div>
          <div className="text-2xl font-bold">{formatTotals(totalValue)}</div>
        </div>
        <div className="border border-border bg-card p-4">
          <div className="pb-2">
            <span className="text-sm font-medium">{t('consignment.detail.soldValue')}</span>
          </div>
          <div className="text-2xl font-bold text-green-600">{formatTotals(soldValue)}</div>
        </div>
        <div className="border border-border bg-card p-4">
          <div className="pb-2">
            <span className="text-sm font-medium">{t('consignment.detail.remainingValue')}</span>
          </div>
          <div className="text-2xl font-bold text-blue-600">{formatTotals(remainingValue)}</div>
        </div>
        <div className="border border-border bg-card p-4">
          <div className="pb-2">
            <span className="text-sm font-medium">{t('consignment.detail.commissionRate')}</span>
          </div>
          <div className="text-2xl font-bold">
            {consignment.commissionRate ? `${consignment.commissionRate}%` : '-'}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="space-y-4">
        <div className="border-b">
          <nav className="flex gap-4">
            {([
              { id: 'items' as const, label: t('consignment.detail.items'), count: consignment.items.length, icon: Package },
              { id: 'sales' as const, label: t('consignment.detail.sales'), count: consignment.sales.length, icon: DollarSign },
              { id: 'settlements' as const, label: t('consignment.detail.settlements'), count: consignment.settlements.length, icon: FileText },
            ]).map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 border-b-2 px-4 py-3 text-sm font-medium whitespace-nowrap transition-colors ${
                  activeTab === tab.id
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                <tab.icon className="h-4 w-4" />
                {tab.label} ({tab.count})
              </button>
            ))}
          </nav>
        </div>

        {activeTab === 'items' && (
          <div className="border border-border bg-card">
            <div className="p-4 border-b border-border">
              <h3 className="text-lg font-semibold">{t('consignment.detail.items')}</h3>
              <p className="text-sm text-muted-foreground">{t('consignment.list.subtitle')}</p>
            </div>
            <div className="p-4">
              {consignment.items.length === 0 ? (
                <p className="text-center py-8 text-muted-foreground">
                  {t('consignment.detail.noItems')}
                </p>
              ) : (
                <table className="w-full">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="px-4 py-2 text-left text-sm font-medium">{t('consignment.form.product')}</th>
                      <th className="px-4 py-2 text-right text-sm font-medium">{t('consignment.form.quantity')}</th>
                      <th className="px-4 py-2 text-right text-sm font-medium">{t('consignment.sale.quantitySold')}</th>
                      <th className="px-4 py-2 text-right text-sm font-medium">{t('consignment.sale.available')}</th>
                      <th className="px-4 py-2 text-right text-sm font-medium">{t('consignment.form.unitCost')}</th>
                      <th className="px-4 py-2 text-right text-sm font-medium">{t('consignment.form.sellingPrice')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {consignment.items.map((item) => {
                      const available =
                        Number(item.quantitySent) - Number(item.quantitySold) - Number(item.quantityReturned);
                      return (
                        <tr key={item.id} className="border-b">
                          <td className="px-4 py-2">
                            <div>
                              <div className="font-medium">{item.product.name}</div>
                              <div className="text-xs text-muted-foreground">
                                {item.product.model.brand.name} - {item.product.model.name}
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-2 text-right">{Number(item.quantitySent)}</td>
                          <td className="px-4 py-2 text-right text-green-600">{Number(item.quantitySold)}</td>
                          <td className="px-4 py-2 text-right text-blue-600">{available}</td>
                          <td className="px-4 py-2 text-right">
                            {formatCurrency(Number(item.unitCost), item.currency)}
                          </td>
                          <td className="px-4 py-2 text-right">
                            {formatCurrency(Number(item.sellingPrice), item.currency)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}

        {activeTab === 'sales' && (
          <div className="border border-border bg-card">
            <div className="p-4 border-b border-border">
              <h3 className="text-lg font-semibold">{t('consignment.detail.sales')}</h3>
            </div>
            <div className="p-4">
              {consignment.sales.length === 0 ? (
                <p className="text-center py-8 text-muted-foreground">
                  {t('consignment.sale.noItemsToSell')}
                </p>
              ) : (
                <div className="space-y-4">
                  {consignment.sales.map((sale) => (
                    <div key={sale.id} className="border border-border p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <span className="font-medium">{sale.saleNumber}</span>
                          <span className="text-sm text-muted-foreground ml-2">
                            {formatDate(sale.saleDate)}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">
                            {formatCurrency(Number(sale.totalAmount), sale.currency)}
                          </span>
                          <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium ${
                            sale.status === 'SETTLED' ? 'bg-primary text-primary-foreground' : 'bg-secondary text-secondary-foreground'
                          }`}>
                            {sale.status}
                          </span>
                        </div>
                      </div>
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b bg-muted/50">
                            <th className="py-2 text-left">{t('consignment.form.product')}</th>
                            <th className="py-2 text-right">{t('consignment.sale.quantitySold')}</th>
                            <th className="py-2 text-right">{t('consignment.sale.unitPrice')}</th>
                            <th className="py-2 text-right">{tCommon('total')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {sale.items.map((item) => (
                            <tr key={item.id} className="border-b">
                              <td className="py-2">{item.consignmentItem.product.name}</td>
                              <td className="py-2 text-right">{Number(item.quantity)}</td>
                              <td className="py-2 text-right">
                                {formatCurrency(Number(item.unitPrice), sale.currency)}
                              </td>
                              <td className="py-2 text-right">
                                {formatCurrency(Number(item.totalAmount), sale.currency)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'settlements' && (
          <div className="border border-border bg-card">
            <div className="p-4 border-b border-border">
              <h3 className="text-lg font-semibold">{t('consignment.settlement.title')}</h3>
            </div>
            <div className="p-4">
              {consignment.settlements.length === 0 ? (
                <p className="text-center py-8 text-muted-foreground">
                  {t('consignment.settlement.pending')}
                </p>
              ) : (
                <table className="w-full">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="px-4 py-2 text-left text-sm font-medium">#</th>
                      <th className="px-4 py-2 text-right text-sm font-medium">
                        {t('consignment.settlement.amountDue')}
                      </th>
                      <th className="px-4 py-2 text-right text-sm font-medium">
                        {t('consignment.settlement.amountPaid')}
                      </th>
                      <th className="px-4 py-2 text-left text-sm font-medium">
                        {t('consignment.settlement.dueDate')}
                      </th>
                      <th className="px-4 py-2 text-left text-sm font-medium">{t('consignment.list.status')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {consignment.settlements.map((settlement) => {
                      const isPaid = Number(settlement.amountPaid) >= Number(settlement.amountDue);
                      return (
                        <tr key={settlement.id} className="border-b">
                          <td className="px-4 py-2 font-medium">{settlement.settlementNumber}</td>
                          <td className="px-4 py-2 text-right">
                            {formatCurrency(Number(settlement.amountDue), settlement.currency)}
                          </td>
                          <td className="px-4 py-2 text-right text-green-600">
                            {formatCurrency(Number(settlement.amountPaid), settlement.currency)}
                          </td>
                          <td className="px-4 py-2">
                            {settlement.dueDate ? formatDate(settlement.dueDate) : '-'}
                          </td>
                          <td className="px-4 py-2">
                            <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium ${
                              isPaid ? 'bg-primary text-primary-foreground' : 'bg-secondary text-secondary-foreground'
                            }`}>
                              {isPaid ? t('consignment.settlement.paid') : t('consignment.settlement.pending')}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      <SendConsignmentModal
        open={sendModalOpen}
        onOpenChange={setSendModalOpen}
        consignmentId={consignment.id}
        onSuccess={() => {
          refetch();
          setSendModalOpen(false);
        }}
      />

      <RecordSaleModal
        open={saleModalOpen}
        onOpenChange={setSaleModalOpen}
        consignment={consignment as unknown as { id: string; items: Array<{ id: string; productId: string; quantitySent: number | string; quantitySold: number | string; quantityReturned: number | string; sellingPrice: number | string; currency: string; product: { name: string; model: { name: string; brand: { name: string } } } }> }}
        onSuccess={() => {
          refetch();
          setSaleModalOpen(false);
        }}
      />
    </div>
  );
}
